import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { GoogleLogin } from "@react-oauth/google";
import { QrCode, BarChart3, Zap, Download, Shield, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast, Toaster } from "sonner";

const features = [
  {
    icon: QrCode,
    title: "Dynamic QR Codes",
    desc: "Create QR codes for URLs, phone numbers, WhatsApp, and vCards. Update destinations anytime.",
    span: "md:col-span-7",
  },
  {
    icon: BarChart3,
    title: "Real-time Analytics",
    desc: "Track every scan with device, browser, and location data.",
    span: "md:col-span-5",
  },
  {
    icon: Zap,
    title: "Instant Redirect",
    desc: "Sub-second redirects with global edge network.",
    span: "md:col-span-4",
  },
  {
    icon: Download,
    title: "Download & Share",
    desc: "Export QR codes as high-res PNG or SVG images.",
    span: "md:col-span-4",
  },
  {
    icon: Shield,
    title: "Enterprise Ready",
    desc: "Team management, API access, and white-label options.",
    span: "md:col-span-4",
  },
];

export default function LandingPage() {
  const { user, processGoogleCredential, devLogin } = useAuth();
  const navigate = useNavigate();
  const canUseGoogle = Boolean(process.env.REACT_APP_GOOGLE_CLIENT_ID);

  if (user) {
    navigate("/dashboard", { replace: true });
    return null;
  }

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      await processGoogleCredential(credentialResponse.credential);
      navigate("/dashboard");
    } catch {
      toast.error("Authentication failed. Please try again.");
    }
  };

  const handleDevLogin = async () => {
    try {
      await devLogin();
      navigate("/dashboard");
    } catch {
      toast.error("Dev login failed. Enable DEV_AUTH on the backend.");
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Toaster richColors position="top-right" />
      {/* Navbar */}
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-zinc-200/50 saturate-150">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#0047FF]">
              <QrCode className="h-4 w-4 text-white" strokeWidth={2} />
            </div>
            <span className="text-lg font-bold tracking-tight text-zinc-950" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              SKA QR
            </span>
          </div>
          <div data-testid="navbar-google-login">
            {canUseGoogle ? (
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => toast.error("Login failed")}
                theme="outline"
                shape="pill"
                size="medium"
                text="signin_with"
              />
            ) : (
              <Button onClick={handleDevLogin} variant="outline">
                Continue (Dev)
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="max-w-3xl"
          >
            <Badge variant="secondary" className="mb-6 text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 bg-zinc-100 border-0">
              Now in beta
            </Badge>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-none text-zinc-950"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              Dynamic QR Codes
              <br />
              <span className="text-[#0047FF]">for Modern Business</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-zinc-500 leading-relaxed max-w-xl">
              Create, track, and manage QR codes with powerful real-time analytics.
              Built for teams that need actionable scan data.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-6">
              <div data-testid="hero-google-login">
                {canUseGoogle ? (
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={() => toast.error("Login failed")}
                    theme="filled_blue"
                    shape="pill"
                    size="large"
                    text="continue_with"
                    width="280"
                  />
                ) : (
                  <Button onClick={handleDevLogin} className="h-12 px-8">
                    Continue (Dev)
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <p className="text-sm text-zinc-400">No credit card required</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Bento Grid */}
      <section className="py-24 md:py-32 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4">Features</p>
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-zinc-950"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              Everything you need to manage QR codes
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={`${f.span} bg-white border border-zinc-200 rounded-xl p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all duration-300 group`}
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-zinc-100 mb-5 group-hover:bg-[#0047FF]/10 transition-colors">
                  <f.icon className="h-5 w-5 text-zinc-600 group-hover:text-[#0047FF] transition-colors" strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-bold text-zinc-950 mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  {f.title}
                </h3>
                <p className="text-sm text-zinc-500 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-zinc-950 mb-6"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            Ready to get started?
          </h2>
          <p className="text-base text-zinc-500 mb-10 max-w-md mx-auto">
            Join thousands of businesses using SKA QR for dynamic QR code management.
          </p>
          <div className="flex justify-center" data-testid="cta-google-login">
            {canUseGoogle ? (
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => toast.error("Login failed")}
                theme="filled_blue"
                shape="pill"
                size="large"
                text="signup_with"
                width="300"
              />
            ) : (
              <Button onClick={handleDevLogin} className="h-12 px-10">
                Continue (Dev)
                <ArrowRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-200 py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
          <div className="flex flex-col gap-1">
            <p className="text-sm text-zinc-400">SKA QR. Generate your QR, Scan and Analytics.</p>
            <p className="text-xs text-zinc-400">© {new Date().getFullYear()} SKA Technologies. All rights reserved.</p>
          </div>
          <a
            className="text-sm text-zinc-400 hover:text-zinc-600 transition-colors"
            href="https://www.skatechnologies.com"
            target="_blank"
            rel="noreferrer"
          >
            www.skatechnologies.com
          </a>
        </div>
      </footer>
    </div>
  );
}
